

import { BrowserDetection } from '@jitsi/js-utils';
import i18next from 'i18next';
import { sha256 } from 'js-sha256';
import moment from 'moment';

import {
    CHROME,
    FIREFOX,
    INTERNET_EXPLORER,
    OPERA,
    SAFARI
} from '../../../../browser';
import {
    ACTION_PINNED,
    ACTION_UNPINNED,
    createOfferAnswerFailedEvent,
    createPinnedEvent,
    sendAnalytics
} from '../../analytics';
import { maybeRedirectToWelcomePage, reloadNow } from '../../app/actions';
import { handlePinParticipantFilmstrip } from '../../conference/filmStripViewManager';
import { openDisplayNamePrompt } from '../../display-name/actions';
import { openLobbyScreen } from '../../lobby/actions';
import {
    NOTIFICATION_TIMEOUT_TYPE,
    hideNotification,
    showErrorNotification,
    showNotification,
    showWarningNotification
} from '../../notifications';
import { closeCaptionsModal, setRequestingSubtitles } from '../../subtitles/actions';
import { setCurrentNotificationUid } from '../../talk-while-muted/actions';
import { iAmVisitor } from '../../visitors/functions';
import { overwriteConfig } from '../config';
import { apiKEY } from '../config/constants';
import { CONNECTION_ESTABLISHED, CONNECTION_FAILED } from '../connection/actionTypes';
import { connectionDisconnected } from '../connection/actions.any';
import { SET_USER_SETTING } from '../jwt';
import { MHConferenceErrors, browser } from '../lib-meet-hour';
import { _autoLiveStreaming, setAttendanceApi } from '../mtApi/actions';
import { fetchAxiosApi } from '../mtApi/functions';
import { PARTICIPANT_UPDATED, PIN_PARTICIPANT } from '../participants/actionTypes';
import { PARTICIPANT_ROLE } from '../participants/constants';
import {
    getLocalParticipant,
    getParticipantById,
    getParticipantDisplayName,
    getPinnedParticipant,
    isLocalParticipantModerator
} from '../participants/functions';
import { MiddlewareRegistry, StateListenerRegistry } from '../redux';
import { getModeratorOptions } from '../sounds/functions.any';
import { TRACK_ADDED, TRACK_REMOVED } from '../tracks/actionTypes';
import { recordMediaRelayRequired } from '../util';
import { checkIfNotReactNative, checkIfReactNative, isDomainWeborNative } from '../util/checkOS';

import {
    CONFERENCE_FAILED,
    CONFERENCE_JOINED,
    CONFERENCE_SUBJECT_CHANGED,
    CONFERENCE_WILL_LEAVE,
    FORCE_MUTE_REMOTE_PARTICIPANT,
    FORCE_MUTE_REMOTE_PARTICIPANT_VIDEO,
    MODERATOR_MUTED_US_ALL,
    SEND_TONES,
    SET_PENDING_SUBJECT_CHANGE,
    SET_ROOM,
    TRANSPORT_STATE_CHANGED
} from './actionTypes';
import {
    conferenceFailed,
    conferenceTimestampChanged,
    conferenceWillLeave,
    createConference,
    moderatorForceMuted,
    setSubject,
    setTimeOfUser,
    updateConferenceCallEnded
} from './actions';
import { primeConferenceInfo } from './conferenceInfoWatch';
import {
    _addLocalTracksToConference,
    _removeLocalTracksFromConference,
    forEachConference,
    getCurrentConference
} from './functions';
import logger from './logger';


declare var APP: Object;

/**
 * Maps the names of the browsers from bowser to the internal names defined in
 * ./browsers.js.
 */
const bowserNameToJitsiName = {
    'Chrome': CHROME,
    'Chromium': CHROME,
    'Opera': OPERA,
    'Firefox': FIREFOX,
    'Internet Explorer': INTERNET_EXPLORER,
    'Safari': SAFARI
};

/**
 * Handler for before unload event.
 */
let beforeUnloadHandler;

const FORE_MUTE_SENT = 'FORCE_MUTE_SENT';
let _lastTransportNotificationFingerprint;
const TRANSPORT_DEBUG_HISTORY_KEY = 'mh_transport_transition_history';
const TRANSPORT_DEBUG_EVENT_NAME = 'mh-transport-debug-event';
const TRANSPORT_DEBUG_CHANNEL_NAME = 'mh-transport-debug-channel';
const TRANSPORT_DEBUG_HISTORY_LIMIT = 50;

/**
 * Implements the middleware of the feature base/conference.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */
MiddlewareRegistry.register(store => next => action => {
    const { getState } = store;
    const state = getState();
    const conference = getCurrentConference(state);

    switch (action.type) {
    case CONFERENCE_FAILED:
        return _conferenceFailed(store, next, action);

    case CONFERENCE_JOINED:
        return _conferenceJoined(store, next, action);

    case CONNECTION_ESTABLISHED:
        return _connectionEstablished(store, next, action);

    case CONNECTION_FAILED:
        return _connectionFailed(store, next, action);

    case CONFERENCE_SUBJECT_CHANGED:
        return _conferenceSubjectChanged(store, next, action);

    case CONFERENCE_WILL_LEAVE:
        _conferenceWillLeave();
        break;

    case FORCE_MUTE_REMOTE_PARTICIPANT: {
        const result = next(action);

        if (conference) {
            // Only send individual force mute command if this is NOT part of a global "Mute All" action.
            // When doing a global "Mute All", _moderatedMute sends a single consolidated update at the end.
            if (!action.isMuteAll) {
                _forceMuteSent(action, conference, getState());
            }

            // Then mute the participant (jid, mediaType)
            if (action.bool !== false) {
                conference.muteParticipant(action.id, action.mediaType);
            }
        }

        return result;
    }

    case FORCE_MUTE_REMOTE_PARTICIPANT_VIDEO: {
        const result = next(action);

        if (conference) {
            // Send the force mute command FIRST to update state across all participants
            _forceMuteSent(action, conference, getState());

            // Then mute the participant (jid, mediaType)
            if (action.bool !== false) {
                conference.muteParticipant(action.id, action.mediaType);
            }
        }

        return result;
    }

    case MODERATOR_MUTED_US_ALL:
        return _moderatedMute(action, conference, store);

    case PARTICIPANT_UPDATED:
        return _updateLocalParticipantInConference(store, next, action);

    case PIN_PARTICIPANT: {
        handlePinParticipantFilmstrip(store.dispatch, state, 'PIN_PARTICIPANT_middleware', action.participant);

        return _pinParticipant(store, next, action);
    }


    case SEND_TONES:
        return _sendTones(store, next, action);

    case SET_ROOM:
        return _setRoom(store, next, action);

    case SET_USER_SETTING: {
        const result = next(action);

        // Settings can arrive after the moderator role (users without a JWT
        // fetch them after the join), so auto start is retried here.
        _autoTranscription(store);

        return result;
    }

    case TRACK_ADDED:
    case TRACK_REMOVED:
        return _trackAddedOrRemoved(store, next, action);

    case TRANSPORT_STATE_CHANGED:
        return _transportStateChanged(store, next, action);
    }

    return next(action);
});

/**
 * Shows a cross-platform user notification when transport status shifts into
 * a degraded or relay fallback state.
 *
 * @param {Store} store - Redux store.
 * @param {Function} next - Next dispatch function.
 * @param {Object} action - Transport state action.
 * @returns {*}
 */
function _transportStateChanged(store, next, action) {
    const result = next(action);
    const { transportState } = action;
    const config = store.getState()['features/base/config'] || {};
    const debugEnabled = config.runtimeTransportDebugEnabled !== false;

    if (!transportState?.userVisibleType) {
        _recordTransportTransition(transportState, { debugEnabled });

        return result;
    }

    _maybeApplyTransportRecoveryProfile(store, transportState);
    _recordTransportTransition(transportState, { debugEnabled });

    const fingerprint = [
        transportState.userVisibleType,
        transportState.reason,
        transportState.signalingStatus,
        transportState.mediaStatus,
        transportState.relayProtocol
    ].join(':');

    if (_lastTransportNotificationFingerprint === fingerprint) {
        return result;
    }

    _lastTransportNotificationFingerprint = fingerprint;

    const notificationProps = _getTransportNotificationProps(transportState);

    if (!notificationProps) {
        return result;
    }

    store.dispatch(showWarningNotification(notificationProps, NOTIFICATION_TIMEOUT_TYPE.STICKY));

    return result;
}

/**
 * Records transport transitions for live debugging and support diagnostics.
 *
 * @param {Object} transportState - Normalized transport state.
 * @param {Object} options - Additional options.
 * @param {boolean} options.debugEnabled - Whether transition telemetry is enabled.
 * @returns {void}
 */
function _recordTransportTransition(transportState = {}, options = {}) {
    const { debugEnabled = true } = options;

    if (!debugEnabled || typeof window === 'undefined' || !transportState || !Object.keys(transportState).length) {
        return;
    }

    const entry = {
        activeTransportLabel: transportState.activeTransportLabel,
        forceTurnRelay: transportState.forceTurnRelay,
        mediaStatus: transportState.mediaStatus,
        p2pActive: transportState.p2pActive,
        reason: transportState.reason,
        relayProtocol: transportState.relayProtocol,
        signalingPath: transportState.signalingPath,
        signalingStatus: transportState.signalingStatus,
        source: transportState.source,
        updatedAt: transportState.updatedAt || Date.now(),
        userVisibleType: transportState.userVisibleType,
        websocketStatus: transportState.websocketStatus
    };
    const comparableEntry = {
        ...entry,
        updatedAt: undefined
    };

    try {
        const rawHistory = window.localStorage?.getItem(TRANSPORT_DEBUG_HISTORY_KEY);
        const history = rawHistory ? JSON.parse(rawHistory) : [];
        const previousEntry = Array.isArray(history) && history.length ? history[history.length - 1] : undefined;
        const comparablePreviousEntry = previousEntry
            ? {
                ...previousEntry,
                updatedAt: undefined
            }
            : undefined;

        if (comparablePreviousEntry
                && JSON.stringify(comparablePreviousEntry) === JSON.stringify(comparableEntry)) {
            return;
        }

        const historyList = Array.isArray(history) ? history : [];

        const nextHistory = [ ...historyList, entry ]
            .slice(-TRANSPORT_DEBUG_HISTORY_LIMIT);

        window.localStorage?.setItem(TRANSPORT_DEBUG_HISTORY_KEY, JSON.stringify(nextHistory));
    } catch (error) {
        logger.warn('Failed to persist transport transition history.', error);
    }

    try {
        window.dispatchEvent(new CustomEvent(TRANSPORT_DEBUG_EVENT_NAME, { detail: entry }));
    } catch (error) {
        logger.warn('Failed to emit transport debug event.', error);
    }

    try {
        const channel = new window.BroadcastChannel(TRANSPORT_DEBUG_CHANNEL_NAME);

        channel.postMessage(entry);
        channel.close();
    } catch {
        // BroadcastChannel is optional; localStorage history remains the fallback.
    }
}

/**
 * Applies shared config fallbacks after transport classification so the next
 * reconnect path behaves consistently on web and native.
 *
 * @param {Store} store - Redux store.
 * @param {Object} transportState - Normalized transport state.
 * @returns {void}
 */
function _maybeApplyTransportRecoveryProfile(store, transportState) {
    const { dispatch, getState } = store;
    const config = getState()['features/base/config'] || {};

    // Only a websocket problem justifies moving signalling to BOSH.
    //
    // 'restricted-network' and 'media-blocked' are MEDIA classifications - ICE could not find a working
    // path. They say nothing about XMPP, which runs over TCP/443 exactly like BOSH does, so a UDP-blocked
    // network is not a reason to abandon a websocket that is working. It is an active downgrade: BOSH
    // long-polling is markedly less reliable across NAT and proxy idle timeouts. Because
    // _forceRelayMediaFallback() dispatches 'restricted-network' after any ICE failure, every ICE failure
    // used to pin signalling to BOSH as a side effect.
    const shouldPreferBosh
        = config.bosh
            && transportState.userVisibleType === 'websocket-fallback'
            && !config.preferBosh;
    const currentP2p = config.p2p || {};

    // The trailing clauses are an idempotence guard: once the relay profile is in place there is nothing
    // left to apply, so re-applying it on every subsequent transport event is pure churn.
    //
    // `config.useTurnUdp !== false` used to be part of that guard, back when the profile below disabled
    // TURN/UDP. It no longer does (deliberately - TURN/UDP is the cheapest relay path), which left the
    // guard permanently unsatisfiable: useTurnUdp stayed truthy, so this stayed true forever. Every
    // transport event of these types then re-dispatched overwriteConfig and re-called
    // recordMediaRelayRequired(), and that call rewrites the flag's timestamp - so on a network that kept
    // producing these events the 30 minute relay TTL was pushed forward continuously and never expired.
    const shouldForceRelay
        = [ 'restricted-network', 'media-blocked' ].includes(transportState.userVisibleType)
            && (!config.forceTurnRelay || currentP2p.iceTransportPolicy !== 'relay');

    if (!shouldPreferBosh && !shouldForceRelay) {
        return;
    }

    const nextConfig = {};

    if (shouldPreferBosh) {
        nextConfig.preferBosh = true;
    }

    if (shouldForceRelay) {
        // Persist the finding. iceTransportPolicy is fixed when the RTCPeerConnection is constructed, so
        // overwriting the redux config only takes effect on the next join - and the reconnect path
        // reloads the page, which discards redux state. Without this the lesson is lost every time and
        // the participant retries with the policy that just failed.
        recordMediaRelayRequired(logger, transportState.userVisibleType);

        nextConfig.forceTurnRelay = true;

        // Do NOT disable TURN/UDP here. On a network that blocks direct peer traffic but permits UDP
        // egress, TURN/UDP is the lowest-latency relay available; removing it forces every such user onto
        // TURN/TCP or TLS, which adds head-of-line blocking to their audio for no benefit. TCP/TLS TURN
        // remains advertised and is still used when UDP is genuinely blocked.
        //
        // Do NOT disable P2P either. A P2P session with iceTransportPolicy=relay reaches the peer over the
        // same TURN server the JVB path would need on this network, one hop earlier and without using
        // bridge capacity. Turning P2P off treated "needs a relay" as "cannot do P2P" and, since the
        // finding is persisted for runtimeForceRelayTtlMs, removed P2P from every 1-1 call in the tab for
        // the next half hour.
        nextConfig.p2p = {
            ...currentP2p,
            iceTransportPolicy: 'relay'
        };
    }

    dispatch(overwriteConfig(nextConfig));
}

/**
 * Builds a user-facing notification from normalized transport state.
 *
 * @param {Object} transportState - Normalized transport state.
 * @returns {?Object}
 */
function _getTransportNotificationProps(transportState) {
    const diagnosticsAction = _buildDiagnosticsAction(transportState.diagnosticsSupported);

    switch (transportState.userVisibleType) {
    case 'restricted-network':
        return {
            ...diagnosticsAction,
            descriptionArguments: { reason: transportState.reason || 'transport-degraded' },
            descriptionKey: 'notify.restrictedNetworkDescription',
            titleKey: 'notify.restrictedNetworkTitle'
        };
    case 'websocket-fallback':
        return {
            ...diagnosticsAction,
            descriptionArguments: { reason: transportState.reason || 'websocket-unavailable' },
            descriptionKey: 'notify.websocketFallbackDescription',
            titleKey: 'notify.websocketFallbackTitle'
        };
    case 'media-blocked':
        return {
            ...diagnosticsAction,
            descriptionArguments: { reason: transportState.reason || 'media-path-blocked' },
            descriptionKey: 'notify.mediaTransportBlockedDescription',
            titleKey: 'notify.mediaTransportBlockedTitle'
        };
    default:
        return null;
    }
}

/**
 * Builds an optional diagnostics action for web environments.
 *
 * @param {boolean} diagnosticsSupported - Whether diagnostics should be shown.
 * @returns {Object}
 */
function _buildDiagnosticsAction(diagnosticsSupported) {
    if (!diagnosticsSupported || typeof window === 'undefined') {
        return {};
    }

    return {
        customActionHandler: () => {
            const token = new URLSearchParams(window.location.search).get('token')
                || new URLSearchParams(window.location.search).get('jwt')
                || '';
            const diagnosticsUrl = token
                ? `/static/transport-diagnostics.html?token=${encodeURIComponent(token)}`
                : '/static/transport-diagnostics.html';

            window.open(diagnosticsUrl, '_blank', 'noopener,noreferrer');

            return true;
        },
        customActionNameKey: 'notify.openDiagnostics'
    };
}

/**
 * Makes sure the Other moderator knows if you force muted or not.
 *
 * @param {Object} action - The redux action {@code FORE_MUTE_SENT} which is
 * being dispatched in the specified {@code store}.
 * @param {Object} conference - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 *  @param {Object} state - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _forceMuteSent(action, conference, state) {
    const localId = getLocalParticipant(state).id;
    const { lockAudio, lockVideo } = state['features/base/conference'];

    if (action.mediaType === 'audio') {
        // Create a new object with the updated value for sending to other moderators
        const updatedLockAudio = {
            ...lockAudio,
            [`${action.id}`]: action.bool
        };

        conference.sendCommand(FORE_MUTE_SENT, {
            attributes: {
                mediaType: action.mediaType,
                remoteAudios: JSON.stringify(updatedLockAudio),
                sentID: localId
            }
        });
    } else {
        const updatedLockVideo = {
            ...lockVideo,
            [`${action.id}`]: action.bool
        };

        conference.sendCommand(FORE_MUTE_SENT, {
            attributes: {
                mediaType: action.mediaType,
                remoteAudios: JSON.stringify(updatedLockVideo),
                sentID: localId
            }
        });
    }
}

/**
 * Makes sure the Other moderator knows if you force muted or not.
 *
 * @param {Object} action - The redux action {@code FORE_MUTE_SENT} which is
 * being dispatched in the specified {@code store}.
 * @param {Object} conference - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @param {Object} state - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _moderatedMute(action, conference, store) {
    const state = store.getState();
    const localId = getLocalParticipant(state).id;
    const { lockAudio, lockVideo } = state['features/base/conference'];
    const isMuteAll = action.isMuteAll;

    if (action?.mediaType === 'audio') {
        if (conference) {
            conference.sendCommand(FORE_MUTE_SENT, {
                attributes: {
                    mediaType: action.mediaType,
                    remoteAudios: JSON.stringify(lockAudio),
                    sentID: localId,
                    isMuteAll
                }
            });
        }
    } else if (conference) {
        conference.sendCommand(FORE_MUTE_SENT, {
            attributes: {
                mediaType: action.mediaType,
                remoteAudios: JSON.stringify(lockVideo),
                sentID: localId,
                isMuteAll
            }
        });
    }
}

/**
 * Set up state change listener to perform maintenance tasks when the conference
 * is left or failed, e.g.
 * Open.
 */
StateListenerRegistry.register(
    state => getCurrentConference(state),
    (conference, store, previousConference) => {
        if (conference && conference !== previousConference) {
            conference.addCommandListener(FORE_MUTE_SENT,
                ({ attributes }) => {
                    const { dispatch, getState } = store;

                    if (attributes) {
                        const { mediaType, sentID, remoteAudios, isMuteAll } = attributes;

                        const localParticipant = getLocalParticipant(getState());

                        if (sentID && sentID !== localParticipant?.id) {

                            dispatch(moderatorForceMuted(mediaType, JSON.parse(remoteAudios), isMuteAll === 'true' || isMuteAll === true, sentID));
                        }

                    }

                }
            );

        }
    });

/**
 * Only a moderator may start the transcriber, and the server grants the role
 * after the join (or at a later promotion), so the join-time auto start runs
 * too early to do anything. Retry it once the role arrives.
 */
StateListenerRegistry.register(
    state => isLocalParticipantModerator(state),
    (isModerator, store, wasModerator) => {
        if (isModerator && !wasModerator) {
            _autoTranscription(store, { moderatorGranted: true });
        }
    });

StateListenerRegistry.register(
    state => state['features/base/conference']?.lockAudio,
    (lockAudio, store, previousLockAudio) => {
        const state = store.getState();
        const localParticipant = getLocalParticipant(state);
        const localId = localParticipant?.id;

        if (localId && !isLocalParticipantModerator(state)) {
            const wasMuted = previousLockAudio?.[localId] === true;
            const isMuted = lockAudio?.[localId] === true;

            if (isMuted && !wasMuted) {
                const { currentNotificationUid } = state['features/talk-while-muted'] || {};

                if (currentNotificationUid) {
                    store.dispatch(hideNotification(currentNotificationUid));
                    store.dispatch(setCurrentNotificationUid());
                }

                const moderatorId = state['features/base/conference']?.audioForceMutedBy;
                const participantDisplayName = moderatorId
                    ? getParticipantDisplayName(state, moderatorId)
                    : 'Moderator';

                store.dispatch(showNotification({
                    descriptionKey: 'notify.forceMutedRemotelyDescription',
                    titleKey: 'notify.forceMutedRemotelyTitle',
                    titleArguments: {
                        participantDisplayName
                    }
                }));
            }
        }
    }
);

StateListenerRegistry.register(
    state => state['features/base/conference']?.lockVideo,
    (lockVideo, store, previousLockVideo) => {
        const state = store.getState();
        const localParticipant = getLocalParticipant(state);
        const localId = localParticipant?.id;

        if (localId && !isLocalParticipantModerator(state)) {
            const wasMuted = previousLockVideo?.[localId] === true;
            const isMuted = lockVideo?.[localId] === true;

            if (isMuted && !wasMuted) {
                const moderatorId = state['features/base/conference']?.videoForceMutedBy;
                const participantDisplayName = moderatorId
                    ? getParticipantDisplayName(state, moderatorId)
                    : 'Moderator';

                store.dispatch(showNotification({
                    descriptionKey: 'notify.forceVideoMutedRemotelyDescription',
                    titleKey: 'notify.forceVideoMutedRemotelyTitle',
                    titleArguments: {
                        participantDisplayName
                    }
                }));
            }
        }
    }
);

/**
 * Makes sure to leave a failed conference in order to release any allocated
 * resources like peer connections, emit participant left events, etc.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {Dispatch} next - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @param {Action} action - The redux action {@code CONFERENCE_FAILED} which is
 * being dispatched in the specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _conferenceFailed({ dispatch, getState }, next, action) {
    const result = next(action);
    const { conference, error } = action;
    const { enableForcedReload } = getState()['features/base/config'];

    // Handle specific failure reasons.
    switch (error.name) {
    case MHConferenceErrors.CONFERENCE_DESTROYED: {
        const [ reason ] = error.params;

        dispatch(showErrorNotification({
            description: reason,
            titleKey: i18next.t('dialog.sessTerminated')
        }));

        // Show the call-ended screen rather than letting the page fall back to
        // whatever it renders without a conference -- which was the prejoin /
        // "waiting in the lobby for the moderator" screen, telling someone whose
        // meeting had just been ended to wait for it to start. Conference.js
        // renders this ahead of every other branch, so setting the flag is what
        // keeps those screens off the page for the few seconds before the
        // redirect below.
        dispatch(updateConferenceCallEnded(true, 'dialog.hangUpLeaveReason'));

        // Executing only for Web => Redirecting to Homepage
        setTimeout(() => {
            if (checkIfNotReactNative()) {
                dispatch(openLobbyScreen(false));
                dispatch(maybeRedirectToWelcomePage({}));
            }
        }, 3000);

        break;
    }
    case MHConferenceErrors.CONFERENCE_RESTARTED: {
        if (enableForcedReload) {
            dispatch(showErrorNotification({
                description: i18next.t('dialog.restartInitiatedBecauseOfBridgeFailure'),
                titleKey: i18next.t('dialog.sessionRestarted')
            }));
        }

        break;
    }
    case MHConferenceErrors.CONNECTION_ERROR: {
        const [ msg ] = error.params;

        dispatch(connectionDisconnected(getState()['features/base/connection'].connection));
        dispatch(showErrorNotification({
            descriptionArguments: { msg },
            descriptionKey: msg ? i18next.t('dialog.connectErrorWithMsg') : i18next.t('dialog.connectError'),
            titleKey: i18next.t('connection.CONNFAIL')
        }));

        break;
    }
    case MHConferenceErrors.DISPLAY_NAME_REQUIRED: {
        if (typeof error.recoverable === 'undefined') {
            error.recoverable = true;
        }

        dispatch(openDisplayNamePrompt(() => {
            const conf = conference
                || getState()['features/base/conference'].joining
                || getState()['features/base/conference'].conference;

            if (conf) {
                conf.join();
            }
        }));
        break;
    }
    case MHConferenceErrors.OFFER_ANSWER_FAILED:
        sendAnalytics(createOfferAnswerFailedEvent());
        break;
    }

    if (typeof APP === 'undefined') {
        !error.recoverable
        && conference
        && conference.leave().catch(reason => {
            // Even though we don't care too much about the failure, it may be
            // good to know that it happen, so log it (on the info level).
            logger.info('MHConference.leave() rejected with:', reason);
        });
    } else if (typeof beforeUnloadHandler !== 'undefined') {
        // FIXME: Workaround for the web version. Currently, the creation of the
        // conference is handled by /conference.js and appropriate failure handlers
        // are set there.
        window.removeEventListener('pagehide', beforeUnloadHandler);
        if (browser?.isFirefox?.() && 'onunload' in window) {
            window.removeEventListener('unload', beforeUnloadHandler);
        }
        beforeUnloadHandler = undefined;
    }

    if (enableForcedReload && error?.name === MHConferenceErrors.CONFERENCE_RESTARTED) {
        dispatch(conferenceWillLeave(conference));
        dispatch(reloadNow());
    }

    return result;
}

/**
 * Does extra sync up on properties that may need to be updated after the
 * conference was joined.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {Dispatch} next - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @param {Action} action - The redux action {@code CONFERENCE_JOINED} which is
 * being dispatched in the specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _conferenceJoined({ dispatch, getState }, next, action) {
    const result = next(action);
    const { conference } = action;
    const { pendingSubjectChange } = getState()['features/base/conference'];
    const { requireDisplayName } = getState()['features/base/config'];

    const state = getState();
    const currentTimestamp = state['features/base/conference'].conferenceTimestamp;

    if (!currentTimestamp || currentTimestamp === 0) {
        dispatch(conferenceTimestampChanged(new Date().getTime()));
    }

    // Re-attach any local tracks that already have an mhTrack in redux state but were not
    // added to the new conference. This is the leave-and-stay-in-app rejoin path on
    // Windows where useAudioStream/useVideoStream enqueue a replaceLocalTrack task while
    // the previous conference is still being torn down; the task drains after the new
    // conference is created but the conference reference captured in the queue's closure
    // (module-level `room` in conference.js) still points at the old (disposed) MHConference
    // and replaceLocalTrack's `resolvedConference` is null, so the redux state was updated
    // but the new conference's MHConference was never told about the track. The mic/camera
    // indicator goes unmuted (the track IS in conference.localTracks map for the OLD
    // conference, and IS in redux state) but the NEW conference has no sender for it, so
    // remote participants receive silence/black. Re-attach here by reading local tracks
    // from redux and running _addLocalTracksToConference on each one the new conference
    // does not already hold.
    try {
        const { getLocalTracks } = require('../tracks/functions');

        const reduxLocalTracks = getLocalTracks(state['features/base/tracks']);
        const conferenceLocalTracks = conference.getLocalTracks?.() ?? [];
        const conferenceTrackIds = new Set(conferenceLocalTracks.map(t => t));

        const orphaned = reduxLocalTracks.filter(t => t.mhTrack
            && !t.mhTrack.disposed
            && !conferenceTrackIds.has(t.mhTrack));

        if (orphaned.length > 0) {
            logger.warn(
                `[DIAG] _conferenceJoined: re-attaching ${orphaned.length} local track(s)`
                + ' that were orphaned by an earlier replaceLocalTrack with no conference.'
                + ` mediaTypes=[${orphaned.map(t => t.mediaType).join(',')}]`);
            _addLocalTracksToConference(conference, orphaned.map(t => t.mhTrack));
        }
    } catch (error) {
        // Never let the diagnostic/re-attach path block the rest of the join handler
        // (display name prompt, attendance, live-streaming, etc).
        logger.error('[DIAG] _conferenceJoined: orphan-track re-attach failed', error);
    }

    if (!iAmVisitor(getState())) {
        // if a visitor is promoted back to main room and want to join an empty breakout room
        // we need to send iq to jicofo, so it can join/create the breakout room
        dispatch(overwriteConfig({ disableFocus: false }));
    }

    pendingSubjectChange && dispatch(setSubject(pendingSubjectChange));

    // FIXME: Very dirty solution. This will work on web only.
    // When the user closes the window or quits the browser, lib-meet-hour
    // handles the process of leaving the conference. This is temporary solution
    // that should cover the described use case as part of the effort to
    // implement the conferenceWillLeave action for web.
    beforeUnloadHandler = () => {
        dispatch(conferenceWillLeave(conference));
    };
    window.addEventListener('pagehide', beforeUnloadHandler);
    if (browser?.isFirefox?.() && 'onunload' in window) {
        window.addEventListener('unload', beforeUnloadHandler);
    }

    if (requireDisplayName
        && !getLocalParticipant(getState)?.name
        && !getState()['features/base/settings']?.displayName
        && !conference.isHidden()) {
        dispatch(openDisplayNamePrompt(undefined));
    }

    _sendUserAttendance({ dispatch,
        getState }, action);

    _autoLiveStreaming({
        dispatch,
        getState
    });

    // A moderator moving to another room (breakout, reconnect) keeps the
    // role, so the role listener does not fire for the new room.
    _autoTranscription({
        dispatch,
        getState
    });

    return result;
}

/**
 * Notifies the feature base/conference that the action
 * {@code CONNECTION_ESTABLISHED} is being dispatched within a specific redux
 * store.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {Dispatch} next - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @param {Action} action - The redux action {@code CONNECTION_ESTABLISHED}
 * which is being dispatched in the specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _connectionEstablished({ dispatch }, next, action) {
    const result = next(action);

    // FIXME: Workaround for the web version. Currently, the creation of the
    // conference is handled by /conference.js.
    if (typeof APP === 'undefined') {
        dispatch(createConference());
    }

    return result;
}

/**
 * Notifies the feature base/conference that the action
 * {@code CONNECTION_FAILED} is being dispatched within a specific redux
 * store.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {Dispatch} next - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @param {Action} action - The redux action {@code CONNECTION_FAILED} which is
 * being dispatched in the specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _connectionFailed({ dispatch, getState }, next, action) {
    const result = next(action);

    if (typeof beforeUnloadHandler !== 'undefined' && typeof window.removeEventListener === 'function') {
        window.removeEventListener('pagehide', beforeUnloadHandler);
        if (browser?.isFirefox?.() && 'onunload' in window) {
            window.removeEventListener('unload', beforeUnloadHandler);
        }
        beforeUnloadHandler = undefined;
    }

    // FIXME: Workaround for the web version. Currently, the creation of the
    // conference is handled by /conference.js and appropriate failure handlers
    // are set there.
    if (typeof APP === 'undefined') {
        const { connection } = action;
        const { error } = action;

        forEachConference(getState, conference => {
            // It feels that it would make things easier if MHConference
            // in lib-meet-hour would monitor it's connection and emit
            // CONFERENCE_FAILED when it's dropped. It has more knowledge on
            // whether it can recover or not. But because the reload screen
            // and the retry logic is implemented in the app maybe it can be
            // left this way for now.
            if (conference.getConnection() === connection) {
                // XXX Note that on mobile the error type passed to
                // connectionFailed is always an object with .name property.
                // This fact needs to be checked prior to enabling this logic on
                // web.
                const conferenceAction
                    = conferenceFailed(conference, error.name);

                // Copy the recoverable flag if set on the CONNECTION_FAILED
                // action to not emit recoverable action caused by
                // a non-recoverable one.
                if (typeof error.recoverable !== 'undefined') {
                    conferenceAction.error.recoverable = error.recoverable;
                }

                dispatch(conferenceAction);
            }

            return true;
        });
    }

    return result;
}

/**
 * Notifies the feature base/conference that the action
 * {@code CONFERENCE_SUBJECT_CHANGED} is being dispatched within a specific
 *  redux store.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {Dispatch} next - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @param {Action} action - The redux action {@code CONFERENCE_SUBJECT_CHANGED}
 * which is being dispatched in the specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _conferenceSubjectChanged({ dispatch, getState }, next, action) {
    const result = next(action);
    const { subject } = getState()['features/base/conference'];

    if (subject) {
        dispatch({
            type: SET_PENDING_SUBJECT_CHANGE,
            subject: undefined
        });
    }

    typeof APP === 'object' && APP.API.notifySubjectChanged(subject);

    return result;
}

/**
 * Notifies the feature base/conference that the action
 * {@code CONFERENCE_WILL_LEAVE} is being dispatched within a specific redux
 * store.
 *
 * @private
 * @returns {void}
 */
function _conferenceWillLeave() {
    if (typeof beforeUnloadHandler !== 'undefined' && typeof window.removeEventListener === 'function') {
        window.removeEventListener('pagehide', beforeUnloadHandler);
        if (browser?.isFirefox?.() && 'onunload' in window) {
            window.removeEventListener('unload', beforeUnloadHandler);
        }
        beforeUnloadHandler = undefined;
    }
}

/**
 * Notifies the feature base/conference that the action {@code PIN_PARTICIPANT}
 * is being dispatched within a specific redux store. Pins the specified remote
 * participant in the associated conference, ignores the local participant.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {Dispatch} next - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @param {Action} action - The redux action {@code PIN_PARTICIPANT} which is
 * being dispatched in the specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _pinParticipant({ getState }, next, action) {
    const state = getState();
    const { conference } = state['features/base/conference'];

    if (!conference) {
        return next(action);
    }

    const participants = state['features/base/participants'];
    const id = action.participant.id;
    const participantById = getParticipantById(participants, id);
    const pinnedParticipant = getPinnedParticipant(participants);
    const actionName = id ? ACTION_PINNED : ACTION_UNPINNED;
    const local
        = (participantById && participantById.local)
            || (!id && pinnedParticipant && pinnedParticipant.local);
    let participantIdForEvent;

    if (local) {
        participantIdForEvent = local;
    } else {
        participantIdForEvent
            = actionName === ACTION_PINNED ? id : pinnedParticipant && pinnedParticipant.id;
    }

    // Only send analytics when we have a valid participant id; otherwise the event is invalid
    if (participantIdForEvent) {
        sendAnalytics(createPinnedEvent(
            actionName,
            participantIdForEvent,
            {
                local,
                'participant_count': conference.getParticipantCount()
            }));
    }

    return next(action);
}

/**
 * Requests the specified tones to be played.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {Dispatch} next - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @param {Action} action - The redux action {@code SEND_TONES} which is
 * being dispatched in the specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _sendTones({ getState }, next, action) {
    const state = getState();
    const { conference } = state['features/base/conference'];

    if (conference) {
        const { duration, tones, pause } = action;

        conference.sendTones(tones, duration, pause);
    }

    return next(action);
}

/**
 * Notifies the feature base/conference that the action
 * {@code SET_ROOM} is being dispatched within a specific
 *  redux store.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {Dispatch} next - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @param {Action} action - The redux action {@code SET_ROOM}
 * which is being dispatched in the specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _setRoom({ dispatch, getState }, next, action) {
    const state = getState();
    const { subject } = state['features/base/config'];
    const { room } = action;

    if (room) {
        // Set the stored subject.
        dispatch(setSubject(subject));
        dispatch(primeConferenceInfo(room));
    }

    return next(action);
}

/**
 * Synchronizes local tracks from state with local tracks in MHConference
 * instance.
 *
 * @param {Store} store - The redux store.
 * @param {Object} action - Action object.
 * @private
 * @returns {Promise}
 */
function _syncConferenceLocalTracksWithState({ getState }, action) {
    const state = getState();
    const conference = getCurrentConference(state);
    let promise;

    if (conference) {
        const track = action.track.mhTrack;

        if (action.type === TRACK_ADDED) {
            // If gUM is slow and tracks are created after the user has already joined the conference, avoid
            // adding the tracks to the conference if the user is a visitor.
            if (!iAmVisitor(state)) {
                promise = _addLocalTracksToConference(conference, [ track ]);
            }
        } else {
            promise = _removeLocalTracksFromConference(conference, [ track ]);
        }
    }

    return promise || Promise.resolve();
}

/**
 * Notifies the feature base/conference that the action {@code TRACK_ADDED}
 * or {@code TRACK_REMOVED} is being dispatched within a specific redux store.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {Dispatch} next - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @param {Action} action - The redux action {@code TRACK_ADDED} or
 * {@code TRACK_REMOVED} which is being dispatched in the specified
 * {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _trackAddedOrRemoved(store, next, action) {
    const track = action.track;

    // TODO All track swapping should happen here instead of conference.js.
    // Since we swap the tracks for the web client in conference.js, ignore
    // presenter tracks here and do not add/remove them to/from the conference.
    if (track && track.local) {
        return (
            _syncConferenceLocalTracksWithState(store, action)
                .then(() => next(action)));
    }

    return next(action);
}

/**
 * Updates the conference object when the local participant is updated.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {Dispatch} next - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @param {Action} action - The redux action which is being dispatched in the
 * specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _updateLocalParticipantInConference({ dispatch, getState }, next, action) {
    const { conference } = getState()['features/base/conference'];
    const { participant } = action;
    const result = next(action);

    const localParticipant = getLocalParticipant(getState);

    if (conference && participant?.id === localParticipant?.id) {
        if ('name' in participant) {
            conference.setDisplayName(participant.name);
        }

        if ('role' in participant && participant.role === PARTICIPANT_ROLE.MODERATOR) {
            const { pendingSubjectChange, subject } = getState()['features/base/conference'];

            // When the local user role is updated to moderator and we have a pending subject change
            // which was not reflected we need to set it (the first time we tried was before becoming moderator).
            if (typeof pendingSubjectChange !== 'undefined' && pendingSubjectChange !== subject) {
                dispatch(setSubject(pendingSubjectChange));
            }
        }
    }

    return result;
}

/**
 *  User Attendance when it joins the conference.
 *
 * @param {any} {getState} - Redux functions.
 * @param {any} action - Redux actions.
 * @returns {any}
 */
async function _sendUserAttendance({ dispatch, getState, isLogout = false }) {
    const state = getState();
    const { API_URL_PREFIX, API_VERSION_PREFIX, MH_CLIENT_ID } = state['features/base/config'];
    const localPart = getLocalParticipant(state)?.name?.toLowerCase();

    if (localPart === 'youtube') {
        return;
    }
    try {
        const format1 = 'YYYY-MM-DD HH:mm:ss';
        const currentDateTime = moment().utc()
        .format(format1);
        const bowser = new BrowserDetection();
        const browserName = bowser.getName();
        let accessType = 'web';
        const participant = getLocalParticipant(state);

        for (const i in bowserNameToJitsiName) {
            if (i !== browserName) {
                accessType = 'app';
            }
        }
        const roomName = getState()['features/base/conference'].room;

        if (checkIfReactNative()) {
            // Native builds this request in base/mtApi/actions instead - the
            // same implementation the hangup path already uses, with the
            // room-name fallbacks and the duplicate guard that the web block below
            // does not have. So rather than duplicating it here, dispatch the
            // action the logout side dispatches and let the mtApi middleware
            // send the login half of the record.
            //
            // Without this, native only ever posted logout_datetime: the
            // conference joined without any matching login row.
            dispatch(setAttendanceApi(false));
        } else {

            let dataJson;

            try {
                const ClientIPData = await fetchAxiosApi('https://api.ipify.org/?format=json', {
                    timeout: 20000,
                    method: 'get',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });

                if (typeof ClientIPData !== 'undefined') {

                    const geoData = await fetchAxiosApi(`${API_URL_PREFIX}${API_VERSION_PREFIX}geolocation?api_key=${apiKEY}&ip_address=${ClientIPData?.ip}`, {
                        timeout: 20000,
                        method: 'get',
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    });

                    dataJson = geoData;
                }
            } catch (error) {
                logger.error('Error in fetching geolocation bros', error);

            }

            const { occurenceId } = state['features/base/mtApi'];

            const moement = moment().seconds(0)
        .milliseconds(0)
        .format('X');

            const mesh256 = sha256(`${apiKEY}:MH:${moement}`);

            let userObject = {};

            if (isLogout === true) {
                userObject = {
                    logout_datetime: currentDateTime

                };
            } else {
                userObject = {
                    login_datetime: currentDateTime

                };
            }

            userObject = {
                ...userObject,
                client_id: MH_CLIENT_ID,
                credentials: mesh256,
                ...isDomainWeborNative,
                meeting_id: roomName,
                name: participant?.name,
                occurrence_id: occurenceId,
                is_host: state['features/base/jwt'].jwt ? 1 : 0,
                email_id: participant?.email,
                login_datetime: currentDateTime,
                user_agent: browserName,
                access_type: accessType,
                ip: dataJson?.IPv4,
                geolocation: dataJson
            };

            const userJson = JSON.stringify(userObject);

            fetchAxiosApi('meeting/save_meeting_attendance', {
                timeout: 20000,
                method: 'post',
                headers: { 'Content-Type': 'application/json' },
                body: userJson,
                config: {
                    API_URL_PREFIX,
                    API_VERSION_PREFIX
                }
            });

        }

        const userDate = new Date();

        dispatch(setTimeOfUser(userDate));

        const { jwt } = state['features/base/jwt'];

        if (!jwt) {

            const settings = await getModeratorOptions(state);

            if (settings) {

                dispatch({ type: SET_USER_SETTING,
                    confSetting: settings });
            }

        }

    // await sendUserData.json();
    } catch {
        logger.error('Error in fetching geolocation');
    }
}

/**
 * Auto starts transcription if AUTO_START_TRANSCRIBE parameter is 1.
 *
 * @param {Object} store - Redux store { dispatch, getState }.
 * @param {Object} [options] - Options.
 * @param {boolean} [options.moderatorGranted] - The local participant has just
 * become moderator.
 * @returns {void}
 */
export function _autoTranscription({ dispatch, getState }, { moderatorGranted = false } = {}) {
    const state = getState();
    const { confSettings, meetingDetails } = state['features/base/jwt'] || {};
    const settings = confSettings || meetingDetails?.settings;
    const autoStartTranscribe = settings?.AUTO_START_TRANSCRIBE;

    if (Number(autoStartTranscribe) !== 1) {
        return;
    }

    // Collapsed by default at join; a later promotion must not close a
    // captions panel the user has since opened.
    if (!moderatorGranted) {
        dispatch(closeCaptionsModal(false));
    }

    const { _requestingSubtitles } = state['features/subtitles'] || {};
    const { isTranscribing } = state['features/transcribing'] || {};
    const conference = getCurrentConference(state);
    const isModerator = isLocalParticipantModerator(state);

    // A request made before the role arrived could not dial, so it does not
    // count once the role is granted; the dial itself still refuses while the
    // transcriber is running.
    const alreadyRequested = _requestingSubtitles && !moderatorGranted;

    if (!isModerator || !conference || alreadyRequested || isTranscribing || conference.getTranscriptionStatus?.() === 'on') {
        return;
    }

    if (Number(settings?.TRANSCRIPTION_QUOTA_EXCEEDED) === 1) {
        dispatch(showErrorNotification({
            descriptionKey: i18next.t('transcribing.transcriptionQuotaExceeded'),
            titleKey: i18next.t('transcribing.transcriptionQuotaExceededTitle')
        }));

        return;
    }

    // The listener's own translation language, never the conference's spoken language: with none picked yet, the
    // subtitles middleware chooses the app language (or English) - see SET_REQUESTING_SUBTITLES there.
    const language = state['features/subtitles']?._language || null;

    dispatch(setRequestingSubtitles(true, Boolean(language), language));
}


